/**
 * `Close this pool`, the Danger zone's second action.
 *
 * Until this shipped the Danger zone had exactly one action, `Hand over the
 * pool`, and a Commissioner alone in a pool they no longer wanted had nowhere
 * to go: handing over needs a successor, and `/pool/leave` refuses an owner.
 * `POST /pool/archive` is the narrow way out, and this is its only entry
 * point.
 *
 * The control is rendered disabled rather than hidden when the pool has other
 * members. A Commissioner looking for a way to get rid of a pool needs to
 * find out that there is one and what it costs; a control that is simply
 * absent reads as the same dead end the screen had before, which is the thing
 * being fixed.
 *
 * Navigation on success is to the pools list, like `LeavePoolButton`: the
 * settings screen it was pressed on belongs to a pool the fan can no longer
 * read, and the generated hook's `/pool/get` invalidation would otherwise
 * re-render this pane into its no-access state.
 */
import { usePoolArchive } from "@sportsmet/pool-sdk/private";
import { Button, confirmDestructive } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  CLOSE_POOL_CONFIRM_BODY,
  CLOSE_POOL_CONFIRM_LABEL,
  CLOSE_POOL_CONFIRM_TITLE,
  CLOSE_POOL_LABEL,
  resolveClosePoolState,
} from "./closePoolState";

export type typeClosePoolButtonProps = {
  poolId: string;
  /** From `POST /pool/get`. Null until that read lands. */
  activeMemberCount: number | null;
  pendingMemberCount: number | null;
};

export const ClosePoolButton = ({
  poolId,
  activeMemberCount,
  pendingMemberCount,
}: typeClosePoolButtonProps): ReactNode => {
  const archive = usePoolArchive();
  const navigate = useNavigate();
  const state = resolveClosePoolState({
    activeMemberCount,
    pendingMemberCount,
  });

  async function handleClose(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: CLOSE_POOL_CONFIRM_TITLE,
      body: CLOSE_POOL_CONFIRM_BODY,
      confirmLabel: CLOSE_POOL_CONFIRM_LABEL,
    });
    if (!isConfirmed) return;

    try {
      const result = await archive.mutateAsync({ poolId });
      if (showApiError(result)) return;
      void navigate({
        to: "/pools",
        search: {
          poolId: undefined,
          q: undefined,
          sheet: undefined,
          dialog: undefined,
          view: undefined,
          panel: undefined,
        },
      });
    } catch {
      showOfflineError();
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="pool-settings-close-pool">
      <Button
        variant="destructive"
        size="md"
        data-testid="pool-settings-close"
        disabled={state.phase !== "ready" || archive.isPending}
        onClick={() => {
          void handleClose();
        }}
      >
        {CLOSE_POOL_LABEL}
      </Button>
      <p
        className="text-caption text-ink-secondary"
        data-testid="pool-settings-close-hint"
      >
        {state.hint}
      </p>
    </div>
  );
};
